// Added by Sejune Oh on 2019-06-25
// Copyright (c) Coreline Soft, All rights are reserved.
//////////////////////////////////////////////////////////////////////////////////////////////////////////////////////
import { Component, RefObject, createRef } from 'react'
import { RouteComponentProps } from 'react-router'
import { inject, observer } from 'mobx-react'
import { observable, action, makeObservable } from 'mobx'
import axios, { AxiosError } from 'axios'

//////////////////////////////////////////////////////////////////////////////////////////////////////////////////////
import { ReactEmptyEvent, ReactClickEvent } from '@/helper/define'
import Intl from '@/helper/Intl'

//////////////////////////////////////////////////////////////////////////////////////////////////////////////////////
import EventStore from '@/stores/EventStore'
import MgmtFormTemplate from './MgmtFormTemplate'
import Logger from '@/helper/Logger'

//////////////////////////////////////////////////////////////////////////////////////////////////////////////////////
// # Mgmt Form Template을 implements 하는 Container들의 Base Props
export interface MgmtFormBaseCProps {
  forwardRef?: RefObject<any>
  onMounted: () => void
  setCurPath: (curPath: Array<[string, string]>) => void
  showSnackbar: (type: 'err' | 'info', message: MgmtError) => void
  showNotFoundDialog: (err: MgmtError, removeRequired?: boolean) => void
  showRemoveDialog: (removeFunc: any, id: string, subDesc?: string) => void
  showRefreshDialog: (message: string) => void
  handleLoadingCover: (flag: boolean) => void
  setSubInfo: (subInfos: Array<MgmtSubInfoProps>) => void
  movePrevPath: () => void
}

interface MgmtFormTemplateCProps<T> extends RouteComponentProps<T> {
  globalEvt?: EventStore
}

export interface MgmtSubInfoProps {
  id?: string
  value?: string
}

export type MgmtError = AxiosError | Error | string

//////////////////////////////////////////////////////////////////////////////////////////////////////////////////////
export default function withMgmtFormTemplate<T>(ChildComponent: any) {
  @inject('globalEvt')
  @observer
  class MgmtFormTemplateContainer extends Component<MgmtFormTemplateCProps<T>> {
    isErrorOccured: boolean = false
    dataNotFounded: boolean = false
    errMessage: string = ''
    _childComponentMounted: boolean = false
    confirmTask?: () => Promise<{}>
    cancelTask?: () => Promise<{}>
    curPath: Array<[string, string]> = []
    isErrNoti: boolean = true
    subInfos: Array<MgmtSubInfoProps> = []
    isSubInfoVisible: boolean = false
    _prevKey?: number

    dialogRef = createRef<any>()
    childComponentRef = createRef<any>()

    constructor(props: MgmtFormTemplateCProps<T>) {
      super(props)
      makeObservable(this, {
        isErrorOccured: observable,
        dataNotFounded: observable,
        errMessage: observable,
        _childComponentMounted: observable,
        confirmTask: observable,
        cancelTask: observable,
        curPath: observable,
        isErrNoti: observable,
        subInfos: observable,
        isSubInfoVisible: observable,
        _prevKey: observable,
        _handlePressEscKey: action,
        _handleSnackbarOpen: action,
        _handleSnackbarClose: action,
        _handleComponentMounted: action,
        _setDialogConfirm: action,
        _setDialogCancel: action,
        _setNotiType: action,
        _setSubInfo: action,
        setCurPath: action,
      })
    }

    //////////////////////////////////////////////////////////////////////////////////////////////////////////////////////
    componentDidMount() {
      if (!this._childComponentMounted) this.props.globalEvt!.setLoadingOn()
      window.addEventListener('keydown', this._handlePressEscKey, true)
    }

    componentWillUnmount() {
      window.removeEventListener('keydown', this._handlePressEscKey, true)
    }

    render() {
      return (
        <MgmtFormTemplate
          {...this.props}
          curPath={this.curPath}
          isErrorOccured={this.isErrorOccured}
          errMessage={this.errMessage}
          onSnackbarClose={this._handleSnackbarClose}
          childComponentMounted={this._childComponentMounted}
          dialogRef={this.dialogRef}
          onDialogConfirm={this.confirmTask!}
          onDialogCancel={this.cancelTask!}
          dataNotFounded={this.dataNotFounded}
          isErrNoti={this.isErrNoti}
          isSubInfoVisible={this.isSubInfoVisible}
          subInfos={this.subInfos}
          onClickSubInfo={this._handleClickSubInfo}
        >
          <ChildComponent
            ref={this.childComponentRef}
            {...this.props}
            onMounted={this._handleComponentMounted}
            setCurPath={this.setCurPath}
            showSnackbar={this._showSnackbar}
            showNotFoundDialog={this._showNotFoundDialog}
            showRemoveDialog={this._showRemoveElementDialog}
            showRefreshDialog={this._showRefreshDialog}
            handleLoadingCover={this._handleLoadingCover}
            setSubInfo={this._setSubInfo}
            movePrevPath={this._moveToPrevPage}
          />
        </MgmtFormTemplate>
      )
    }

    //////////////////////////////////////////////////////////////////////////////////////////////////////////////////////
    _handlePressEscKey = ({ keyCode }: any) => {
      // Dialog가 열려있을 때는 다이얼로그를 닫는 기본동작을 수행.
      const dialogElem = document.querySelector('div[data-dialog-content="visible"]')
      if (dialogElem) return

      // Press ESC Key
      if (keyCode === 27) {
        if (!this.isSubInfoVisible) return this._moveToPrevPage()
        this.isSubInfoVisible = false
        return
      }

      if (this._prevKey === 17 && keyCode === 73) {
        this.isSubInfoVisible = !this.isSubInfoVisible
        return
      }

      this._prevKey = keyCode
    }

    _showSnackbar = (type: 'err' | 'info', msg: MgmtError) => {
      this._setNotiType(type)

      if (typeof msg === 'string') {
        this._handleSnackbarOpen(msg)
        return
      }

      this._handleSnackbarOpen(this._getErrMessage(msg))
    }

    _showNotFoundDialog = (err: MgmtError, removeRequired = true) => {
      this._setDialogConfirm(() => {
        return new Promise((resolve) => {
          this._moveToPrevPage()
          resolve(true)
        })
      })

      this._setDialogCancel(() => {
        return new Promise(async (resolve) => {
          // eslint-disable-next-line
          const [, , storeName, , elemId] = location.pathname.split('/')
          let path = storeName
          if (storeName === 'data') path = 'datastorages'
          else if (storeName === 'apsrule') path = 'aps/rules'

          try {
            await axios.delete(`/v1/mgmt/${path}/${elemId}`)
          } catch (err) {
            Logger.errLog(this._getErrMessage(err))
          } finally {
            this._moveToPrevPage()
            resolve(true)
          }
        })
      })

      const dialogDesc = Intl.getIntlText({ id: 'htmljs_react.mgmt.mgmtformtemplatecontainer.dialog_find_err' })
      const errMessage = this._getErrMessage(err)
      this.dialogRef
        .current!.setDialogInfo({
          title: Intl.getIntlText({ id: 'htmljs_react.mgmt.mgmtformtemplatecontainer.dialog_title_err' }),
          description: `${dialogDesc}/nErr : ${errMessage}`,
          confirmBtn: Intl.getIntlText({ id: 'htmljs_react.mgmt.mgmtformtemplatecontainer.btn_mv_to_list' }),
          cancelBtn: Intl.getIntlText({ id: 'htmljs_react.mgmt.mgmtformtemplatecontainer.btn_remove' }),
          isRemove: true,
          hideCancelBtn: !removeRequired,
          disableBackdropClick: true,
        })
        .handleOpen()

      this.props.globalEvt!.setLoadingOff()
    }

    _showRemoveElementDialog = (removeFunc: any, id: string, subDesc?: string) => {
      this._setDialogConfirm(() => {
        return new Promise(async (resolve) => {
          try {
            await removeFunc(id)
            this._moveToPrevPage()
          } catch (err) {
            const eMsg = this._getErrMessage(err)
            Logger.errLog(eMsg)
            this._showSnackbar('err', `Error : ${eMsg}`)
          } finally {
            resolve(true)
          }
        })
      })

      this._setDialogCancel(() => {
        return new Promise(async (resolve) => {
          resolve(true)
        })
      })

      // prettier-ignore
      const removeDesc = Intl.getIntlText({ id: 'htmljs_react.mgmt.mgmtformtemplatecontainer.dialog_confirm_remove' })
      const description = `${removeDesc}${subDesc ? '/n' + subDesc : ''}`

      if (this.dialogRef.current!) {
        this.dialogRef
          .current!.setDialogInfo({
            title: Intl.getIntlText({ id: 'htmljs_react.mgmt.mgmtformtemplatecontainer.btn_remove' }),
            description,
          })
          .handleOpen()
      }
    }

    _showRefreshDialog = (description: string) => {
      this._setDialogConfirm(() => {
        return new Promise(async (resolve) => {
          // @ts-ignore
          if (window.clapp) window.clapp.reload()
          else {
            // eslint-disable-next-line
            location.reload()
          }
          resolve(true)
        })
      })

      this._setDialogCancel(() => {
        return new Promise(async (resolve) => {
          resolve(true)
        })
      })

      if (this.dialogRef.current!) {
        this.dialogRef
          .current!.setDialogInfo({
            title: Intl.getIntlText({ id: 'htmljs_react.mgmt.mgmtformtemplatecontainer.dialog_title_notice' }),
            description,
            confirmBtn: Intl.getIntlText({ id: 'htmljs_react.mgmt.mgmtformtemplatecontainer.btn_refresh' }),
          })
          .handleOpen()
      }
    }

    _getErrMessage = (err: MgmtError) => {
      try {
        if (typeof err === 'string') return err

        // @ts-ignore
        return err.response.data.message
      } catch (e) {
        // @ts-ignore
        return err.message
      }
    }

    _moveToPrevPage = () => {
      try {
        // @ts-ignore
        const from = this.props.history.location.state.from
        Logger.devLog(`move page > ${from}`)
        this.props.history.goBack()
      } catch (err) {
        const [, prevPath] = this.curPath[this.curPath.length - 2]
        this.props.history.replace(prevPath)
      }
    }

    _handleLoadingCover = (flag: boolean) => {
      if (flag) this.props.globalEvt!.setLoadingOn()
      else this.props.globalEvt!.setLoadingOff()
    }

    _handleClickSubInfo: ReactClickEvent = ({ currentTarget }) => {
      const targetId = currentTarget.dataset.targetId
      if (targetId) {
        const valueInput = document.getElementById(targetId) as HTMLInputElement
        valueInput.select()
        document.execCommand('copy')
        this._showSnackbar('info', Intl.getIntlText({ id: 'htmljs_react.mgmt.mgmtformtemplatecontainer.msg_copied' }))
      }
    }

    //////////////////////////////////////////////////////////////////////////////////////////////////////////////////////
    _handleSnackbarOpen = (errMessage: string) => {
      this.errMessage = errMessage
      this.isErrorOccured = true
    }

    _handleSnackbarClose: ReactEmptyEvent = () => {
      this.errMessage = ''
      this.isErrorOccured = false
    }

    _handleComponentMounted = () => {
      this._childComponentMounted = true
      this.props.globalEvt!.setLoadingOff()
    }

    _setDialogConfirm = (handleDialogConfirm: () => Promise<{}>) => {
      this.confirmTask = handleDialogConfirm
    }

    _setDialogCancel = (handleDialogCancel: () => Promise<{}>) => {
      this.cancelTask = handleDialogCancel
    }

    _setNotiType = (notiType: 'err' | 'info') => {
      this.isErrNoti = notiType === 'err'
    }

    _setSubInfo = (subInfos: Array<MgmtSubInfoProps>) => {
      this.subInfos = subInfos
    }

    setCurPath = (curPath: Array<[string, string]>) => {
      this.curPath = curPath
    }
  }

  return MgmtFormTemplateContainer
}
